import { useState } from 'react';
import { LANGUAGES, type CreateRoomResponse, type Language, type VoiceChoice } from '@voxa/shared';
import { api, ApiError } from '../lib/api.ts';
import { useI18n } from '../i18n/index.tsx';
import { languageOptionLabel, localizedLanguageName } from '../i18n/languageName.ts';
import { VoicePanel, type PanelChoice } from '../components/VoicePanel.tsx';

const BUILT_IN: PanelChoice = { provider: 'openai', voiceId: '', name: '' };

/** What the server is sent for one side. A side without a voice yet is not sendable. */
const toVoice = (c: PanelChoice): VoiceChoice | null =>
  c.provider === 'openai'
    ? { provider: 'openai' }
    : c.voiceId
      ? {
          provider: 'elevenlabs',
          voiceId: c.voiceId,
          ...(c.name && { name: c.name.slice(0, 120) }),
        }
      : null;

/**
 * Steps 1–2: pick the pair, get a link. The pair is fixed for the room's
 * lifetime, so this is the only place it can be chosen.
 */
export function Home({
  onSignOut,
  sourceSubtitlesAvailable,
  voicePickerAvailable,
  onCreated,
}: {
  /** Present when this deployment has a demo code, so there is a way back out. */
  onSignOut?: () => void;
  /**
   * Whether this server will honour a request for the original words. Hiding
   * the control when it will not is the point: a switch that changes nothing
   * is worse than no switch, and the room would come back with it off anyway.
   */
  sourceSubtitlesAvailable: boolean;
  /**
   * Whether there is a voice to pick besides the built-in one (task 6). Without
   * one the screen is exactly what it was before, two language lists side by
   * side; with one, each side gets a panel of its own.
   */
  voicePickerAvailable: boolean;
  onCreated: (room: CreateRoomResponse) => void;
}) {
  const { t, language: ui } = useI18n();
  const [myLanguage, setMyLanguage] = useState<Language>('ru');
  const [peerLanguage, setPeerLanguage] = useState<Language>('es');
  const [sourceSubtitles, setSourceSubtitles] = useState(false);
  const [myVoice, setMyVoice] = useState<PanelChoice>(BUILT_IN);
  const [peerVoice, setPeerVoice] = useState<PanelChoice>(BUILT_IN);
  const voices = voicePickerAvailable ? { mine: toVoice(myVoice), peer: toVoice(peerVoice) } : null;
  // A side set to ElevenLabs whose voice has not arrived cannot be sent as it
  // is, and quietly sending the built-in voice instead would not be what was
  // chosen. So the button waits.
  const voiceMissing = voices !== null && (voices.mine === null || voices.peer === null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateRoomResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const inviteUrl = created
    ? `${location.origin}/join/${created.roomId}#invite=${created.inviteToken}`
    : '';

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const room = await api.createRoom({
        myLanguage,
        peerLanguage,
        // Sent only when asked for, so a server that has never heard of the
        // field behaves exactly as it did before.
        ...(sourceSubtitles ? { sourceSubtitles: true } : {}),
        // Only sent when there was a choice to make, for the same reason.
        ...(voices?.mine && voices.peer
          ? { voices: { mine: voices.mine, peer: voices.peer } }
          : {}),
      });
      setCreated(room);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.home.createFailed);
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    return (
      <div className="card">
        <h1>{t.home.readyTitle}</h1>
        <p className="lede">
          {t.home.youHear(localizedLanguageName(created.myLanguage, ui))}{' '}
          {t.home.theyHear(localizedLanguageName(created.peerLanguage, ui))}{' '}
          {created.sourceSubtitles && t.home.sourceSubtitlesReady}
        </p>

        <div className="field">
          <label htmlFor="invite">{t.home.inviteLabel}</label>
          <div className="invite">
            <input
              id="invite"
              type="text"
              readOnly
              value={inviteUrl}
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(inviteUrl).then(() => setCopied(true));
              }}
            >
              {copied ? t.home.copied : t.home.copy}
            </button>
          </div>
        </div>

        <div className="notice">{t.home.inviteNote}</div>

        <div className="actions">
          <button type="button" className="primary" onClick={() => onCreated(created)}>
            {t.home.goToCheck}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h1>{t.home.title}</h1>
      <p className="lede">{t.home.lede}</p>

      {error && <div className="notice bad">{error}</div>}

      {voicePickerAvailable ? (
        <div className="people">
          <VoicePanel
            title={t.voice.you}
            languageId="mine"
            languageLabel={t.home.myLanguage}
            language={myLanguage}
            onLanguage={setMyLanguage}
            choice={myVoice}
            onChoice={setMyVoice}
          />
          <VoicePanel
            title={t.voice.peer}
            languageId="theirs"
            languageLabel={t.home.peerLanguage}
            language={peerLanguage}
            onLanguage={setPeerLanguage}
            choice={peerVoice}
            onChoice={setPeerVoice}
          />
        </div>
      ) : (
        <div className="row">
          <div className="field">
            <label htmlFor="mine">{t.home.myLanguage}</label>
            <select
              id="mine"
              value={myLanguage}
              onChange={(e) => setMyLanguage(e.target.value as Language)}
            >
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {languageOptionLabel(l, ui)}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="theirs">{t.home.peerLanguage}</label>
            <select
              id="theirs"
              value={peerLanguage}
              onChange={(e) => setPeerLanguage(e.target.value as Language)}
            >
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {languageOptionLabel(l, ui)}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {sourceSubtitlesAvailable && (
        <div className="field">
          <label className="check">
            <input
              type="checkbox"
              checked={sourceSubtitles}
              onChange={(e) => setSourceSubtitles(e.target.checked)}
            />
            <span>{t.home.sourceSubtitles}</span>
          </label>
          <p className="hint">
            {sourceSubtitles ? t.home.sourceSubtitlesOn : t.home.sourceSubtitlesOff}
          </p>
        </div>
      )}

      {myLanguage === peerLanguage && <div className="notice warn">{t.home.samePair}</div>}
      {voiceMissing && <p className="hint">{t.voice.needVoice}</p>}

      <div className="actions">
        <button
          type="button"
          className="primary"
          disabled={busy || myLanguage === peerLanguage || voiceMissing}
          onClick={() => void create()}
        >
          {busy ? t.home.creating : t.home.create}
        </button>
        {onSignOut && (
          <button type="button" onClick={onSignOut}>
            {t.home.signOut}
          </button>
        )}
      </div>
    </div>
  );
}
